import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, LayoutGrid, List } from "lucide-react";
import { useTranslation } from "../context/LanguageContext";
import type { LayoutMode } from "../utils/layoutModePreference";

interface LayoutModeMenuProps {
  mode: LayoutMode;
  onChange: (mode: LayoutMode) => void;
  className?: string;
  /** Icon-only on phones (chevron from sm up), so crowded toolbars fit on one line. */
  compact?: boolean;
}

/** List / grid dropdown: a filled pill showing the current layout, opening a small menu below it. */
export default function LayoutModeMenu({ mode, onChange, className = "", compact = false }: LayoutModeMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const options: { value: LayoutMode; label: string; Icon: typeof List }[] = [
    { value: "list", label: t("card.listShort"), Icon: List },
    { value: "grid", label: t("card.gridShort"), Icon: LayoutGrid },
  ];
  const Current = mode === "grid" ? LayoutGrid : List;

  return (
    <div ref={rootRef} className={`relative flex-shrink-0 ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${t("products.layout")}: ${mode === "grid" ? t("card.gridShort") : t("card.listShort")}`}
        title={t("products.layout")}
        className={`flex h-8 items-center justify-center gap-0.5 rounded-full border border-purple-200 bg-purple-50 text-purple-700 ${compact ? "w-8 sm:w-auto sm:px-2.5" : "px-2.5"} transition-colors hover:bg-purple-100 dark:border-purple-700/60 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50`}
      >
        <Current size={15} fill={mode === "grid" ? "currentColor" : "none"} aria-hidden="true" />
        <ChevronDown size={11} aria-hidden="true" className={compact ? "hidden sm:block" : ""} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1.5 min-w-[150px] rounded-xl border border-gray-200 bg-white p-1 shadow-lg dark:border-dark-700 dark:bg-dark-900"
        >
          {options.map(({ value, label, Icon }) => {
            const active = value === mode;
            return (
              <button
                key={value}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  setOpen(false);
                  if (!active) onChange(value);
                }}
                className={`flex h-10 w-full items-center gap-2.5 rounded-lg px-3 text-left text-sm font-semibold transition-colors ${
                  active
                    ? "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                    : "text-gray-800 hover:bg-gray-50 dark:text-dark-100 dark:hover:bg-dark-800"
                }`}
              >
                <Icon size={16} fill={value === "grid" && active ? "currentColor" : "none"} aria-hidden="true" />
                <span className="flex-1">{label}</span>
                {active && <Check size={15} aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
